import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

/**
 * Custom hook for fetching and managing portfolio data
 * Used by V2 pages for consistent data fetching
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @returns {Object} Portfolio data and utility functions
 */
export function usePortfolio(options = {}) {
  const { autoFetch = true } = options;

  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Track the active request so that rapid navigation / refresh clicks
  // don't let an older response overwrite a newer one (last-request-wins).
  const activeControllerRef = useRef(null);

  const fetchPortfolio = useCallback(async (forceRefresh = false) => {
    // Cancel any in-flight request — its result would be stale.
    if (activeControllerRef.current) {
      activeControllerRef.current.abort();
    }
    const controller = new AbortController();
    activeControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      const url = forceRefresh
        ? `${API_BASE_URL}/portfolio?refresh=true`
        : `${API_BASE_URL}/portfolio`;

      const response = await axios.get(url, {
        withCredentials: true,
        signal: controller.signal,
      });

      // Only commit results if this is still the active request.
      if (activeControllerRef.current !== controller) {
        return null;
      }

      setPortfolio(response.data);
      setLastUpdated(new Date());
      setLoading(false);
      return response.data;
    } catch (err) {
      // Aborted requests are expected — don't surface them as errors.
      if (axios.isCancel?.(err) || err.name === 'CanceledError' || err.name === 'AbortError') {
        return null;
      }
      if (activeControllerRef.current !== controller) {
        return null;
      }
      console.error('Error fetching portfolio:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchPortfolio();
    }
    return () => {
      // Abort any pending request on unmount.
      if (activeControllerRef.current) {
        activeControllerRef.current.abort();
        activeControllerRef.current = null;
      }
    };
  }, [autoFetch, fetchPortfolio]);

  // Filter holdings by category/type
  const getHoldingsByCategory = useCallback((category) => {
    if (!portfolio?.holdings) return [];

    const holdings = portfolio.holdings;

    switch (category) {
      case 'investments':
        return holdings.filter(h =>
          !h.isLiability &&
          ['Stock', 'ETF', 'Cryptocurrency', 'Precious Metal'].includes(h.assetType)
        );
      case 'cash':
        return holdings.filter(h =>
          !h.isLiability &&
          h.assetType === 'Cash/Bank Account'
        );
      case 'real-estate':
        return holdings.filter(h =>
          !h.isLiability &&
          ['Real Estate & Property', 'Real Estate'].includes(h.assetType)
        );
      case 'vehicles':
        return holdings.filter(h =>
          !h.isLiability &&
          ['Vehicle', 'Equipment', 'Other High-Value Asset'].includes(h.assetType)
        );
      case 'liabilities':
        return holdings.filter(h => h.isLiability);
      default:
        return holdings;
    }
  }, [portfolio]);

  // Calculate summary statistics for a set of holdings.
  // IMPORTANT: All values are in EUR. Cost basis uses the backend-provided
  // `purchasePriceEUR` (already converted at purchase time) — NEVER the native
  // `purchasePrice`, which would mix currencies and produce wrong gains for
  // non-EUR holdings. See issue #180.
  const calculateSummary = useCallback((holdings) => {
    if (!holdings || holdings.length === 0) {
      return {
        totalValue: 0,
        totalCost: 0,
        totalGain: 0,
        gainPercent: 0,
        count: 0
      };
    }

    const totalValue = holdings.reduce((sum, h) => sum + (h.totalValueEUR || 0), 0);
    const totalCost = holdings.reduce((sum, h) => {
      const units = parseFloat(h.units) || 0;
      // Use EUR cost basis stored by the backend at purchase time.
      // Fall back to native purchasePrice only when the currency is EUR
      // (legacy records without purchasePriceEUR) to avoid mixed-currency math.
      const purchasePriceEUR = h.purchasePriceEUR != null
        ? parseFloat(h.purchasePriceEUR)
        : (h.currency === 'EUR' ? (parseFloat(h.purchasePrice) || 0) : 0);
      return sum + (units * (purchasePriceEUR || 0));
    }, 0);
    const totalGain = totalValue - totalCost;
    const gainPercent = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;

    return {
      totalValue,
      totalCost,
      totalGain,
      gainPercent,
      count: holdings.length
    };
  }, []);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    if (!portfolio?.holdings) {
      return {
        assetTypes: [],
        platforms: [],
        currencies: []
      };
    }

    const holdings = portfolio.holdings;

    return {
      assetTypes: [...new Set(holdings.map(h => h.assetType).filter(Boolean))].sort(),
      platforms: [...new Set(holdings.map(h => h.platform).filter(Boolean))].sort(),
      currencies: [...new Set(holdings.map(h => h.currency).filter(Boolean))].sort()
    };
  }, [portfolio]);

  // Portfolio summary
  const summary = useMemo(() => {
    if (!portfolio?.summary) return null;
    return portfolio.summary;
  }, [portfolio]);

  // Convenience function that always forces a refresh (bypasses cache)
  const refresh = useCallback(() => fetchPortfolio(true), [fetchPortfolio]);

  return {
    portfolio,
    holdings: portfolio?.holdings || [],
    summary,
    loading,
    error,
    lastUpdated,
    refresh,
    fetchPortfolio,
    getHoldingsByCategory,
    calculateSummary,
    filterOptions
  };
}

export default usePortfolio;
